import { useEffect, useState } from 'react'
import { App } from '@capacitor/app'
import { cachedDeviceWeekStart, hasDeviceClock, readDeviceWeekStart } from '../lib/deviceClock'
import type { WeekStartsOn } from '../lib/eventCalendar'

/**
 * The phone's first day of the week, or null on the web, older shells, and
 * shells that predate the field. Re-read whenever the app returns to the
 * foreground, like `useDeviceClock`.
 */
export function useDeviceWeekStart(): WeekStartsOn | null {
  const [weekStartsOn, setWeekStartsOn] = useState<WeekStartsOn | null>(() =>
    hasDeviceClock() ? cachedDeviceWeekStart() : null,
  )

  useEffect(() => {
    if (!hasDeviceClock()) return
    let disposed = false
    let resumeSub: { remove: () => void } | undefined
    const refresh = () => {
      void readDeviceWeekStart().then(next => {
        if (!disposed && next !== null) setWeekStartsOn(next)
      })
    }
    refresh()
    void App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) refresh()
    }).then(h => {
      if (disposed) h.remove()
      else resumeSub = h
    })
    return () => {
      disposed = true
      resumeSub?.remove()
    }
  }, [])

  return weekStartsOn
}
